/**
 * pmath — portable, bit-exact trigonometry and hypot.
 *
 * WHY THIS EXISTS
 * ---------------
 * Hyperlight derives its entire world as pure functions of seeds, evaluated
 * identically on the client (browser V8) and the server (Node V8). `Math.sin`,
 * `Math.cos` and `Math.hypot` are *implementation-approximated* in ECMA-262:
 * different engines (and even different V8 versions) may return different bits.
 * That silently breaks determinism. This module replaces them with fixed
 * compositions of ops that ECMA-262 specifies to the bit on every conforming
 * engine: `+ - * /`, `Math.sqrt`, `Math.round`/`floor`/`abs`, and bitwise `&`
 * (ToInt32 is exact). No implementation-approximated intrinsic is used.
 *
 * DETERMINISM vs ACCURACY
 * -----------------------
 * Determinism = bit-identity of *this exact op composition* across engines. It
 * does NOT depend on agreeing with `Math.sin`. The accompanying test compares
 * against `Math.*` only as an accuracy ORACLE (target <= 2 ulp for |x| <= 2^26);
 * beyond that range only determinism is promised.
 *
 * PROVENANCE
 * ----------
 * The polynomial kernels and the pi/2 constants are ported from the
 * public-domain FDLIBM (Sun Microsystems, "Freely Distributable LIBM"):
 *   - kernelSin: k_sin.c  (coefficients S1..S6)
 *   - kernelCos: k_cos.c  (coefficients C1..C6)
 *   - argument reduction constants: e_rem_pio2.c (invpio2, pi/2 split)
 * Each block cites its source file. Coefficients are transcribed verbatim
 * (decimal form) from the FDLIBM sources.
 *
 * REDUCTION CHOICE (documented deviation — see report)
 * ----------------------------------------------------
 * The brief sketches a naive 3-term Cody-Waite step
 * `r = ((x - n*PIO2_1) - n*PIO2_2) - n*PIO2_3`. That is only accurate while
 * `n*PIO2_1` is exactly representable (|n| < ~2^22, i.e. |x| < ~1e7); measured
 * error explodes to ~8.8e12 ulp at |x| = 2^26, so it cannot meet the stated
 * <=2 ulp gate over the required |x| <= 2^26 range. Under the brief's sanctioned
 * flexibility ("any fixed exactly-specified composition is acceptable — document
 * the choice") we use an error-compensated Cody-Waite step instead: pi/2 as a
 * (hi,lo) double-double and a Dekker two-product so that `n*(pi/2)` is carried
 * to ~106 bits before the cancelling subtraction from x. This is still a fixed
 * composition of exactly-specified ops, so determinism is preserved, and it
 * measures <= 1 ulp across all three sweep ranges including |x| = 2^26.
 *
 * Task 2 extends this file (atan2/asin/... etc). Keep the layout:
 * constants -> reduction/kernels -> public functions.
 */

// ── constants ─────────────────────────────────────────────────────────────

/** 2*pi as the nearest double. Exported for call sites that wrap angles. */
export const TWO_PI = 6.283185307179586;

// invpio2 (2/pi) is the FDLIBM e_rem_pio2.c constant. PIO2_HI/PIO2_LO are the
// canonical double-double of pi/2 — nearest double + rounded residual, ~106
// bits total (NOT FDLIBM's truncated pio2_1/pio2_1t, which the compensated
// reduction below does not need). Both are verified to round-trip to the hex.
const INV_PIO2 = 6.36619772367581382433e-01; // 2/pi    (0x3FE45F30_6DC9C883)
const PIO2_HI = 1.5707963267948966; //          pi/2 hi  (0x3FF921FB_54442D18)
const PIO2_LO = 6.123233995736766e-17; //        pi/2 lo  (0x3C91A626_33145C07)

// Veltkamp/Dekker splitting constant 2^27 + 1, used by the two-product below.
const SPLIT = 134217729;

// Below this magnitude sin(x) == x to < 0.5 ulp; the early return also
// preserves the sign of a zero input (sin(-0) === -0). 2^-27, per FDLIBM.
const SIN_TINY = 7.450580596923828e-09;

// FDLIBM k_sin.c — sin(x) ~ x + x^3*(S1 + x^2*(S2 + ... + x^10*S6)) on |x|<=pi/4
const S1 = -1.66666666666666324348e-01; // 0xBFC55555_55555549
const S2 = 8.33333333332248946124e-03; //  0x3F811111_1110F8A6
const S3 = -1.98412698298579493134e-04; // 0xBF2A01A0_19C161D5
const S4 = 2.75573137070700676789e-06; //  0x3EC71DE3_57B1FE7D
const S5 = -2.50507602534068634195e-08; // 0xBE5AE5E6_8A2B9CEB
const S6 = 1.58969099521155010221e-10; //  0x3DE5D93A_5ACFD57C

// FDLIBM k_cos.c — cos(x) ~ 1 - x^2/2 + x^4*(C1 + x^2*(C2 + ... + x^8*C6))
const C1 = 4.16666666666666019037e-02; //  0x3FA55555_5555554C
const C2 = -1.38888888888741095749e-03; // 0xBF56C16C_16C15177
const C3 = 2.48015872894767294178e-05; //  0x3EFA01A0_19CB1590
const C4 = -2.75573143513906633035e-07; // 0xBE927E4F_809C52AD
const C5 = 2.08757232129817482790e-09; //  0x3E21EE9E_BDB4B1C4
const C6 = -1.13596475577881948265e-11; // 0xBDA8FAE9_BE8838D4

// ══ Task 2 constants (atan2 / acos / exp / log / pow) ══════════════════════
// pi/2 reuses the existing PIO2_HI/PIO2_LO double-double. TINY only nudges the
// correctly-signed FDLIBM tie result; it does not change the rounded value.
const PI = 3.14159265358979311600e+00; //   e_atan2.c pi     0x400921FB_54442D18
const PI_LO = 1.2246467991473531772e-16; //  e_atan2.c pi_lo  0x3CA1A626_33145C07
const PIO4 = 7.85398163397448278999e-01; //  e_atan2.c pi_o_4 0x3FE921FB_54442D18
const TINY = 1.0e-300; //                    e_atan2.c/e_pow.c tiny

// FDLIBM s_atan.c — atan kernel: aT[0..10] odd-poly coefficients plus the four
// atan(hi/lo) breakpoint constants for the reduction intervals.
const AT0 = 3.33333333333329318027e-01; //  0x3FD55555_5555550D
const AT1 = -1.99999999998764832476e-01; // 0xBFC99999_9998EBC4
const AT2 = 1.42857142725034663711e-01; //  0x3FC24924_920083FF
const AT3 = -1.11111104054623557880e-01; // 0xBFBC71C6_FE231671
const AT4 = 9.09088713343650656196e-02; //  0x3FB745CD_C54C206E
const AT5 = -7.69187620504482999495e-02; // 0xBFB3B0F2_AF749A6D
const AT6 = 6.66107313738753120669e-02; //  0x3FB10D66_A0D03D51
const AT7 = -5.83357013379057348645e-02; // 0xBFADDE2D_52DEFD9A
const AT8 = 4.97687799461593236017e-02; //  0x3FA97B4B_24760DEB
const AT9 = -3.65315727442169155270e-02; // 0xBFA2B444_2C6A6C2F
const AT10 = 1.62858201153657823623e-02; // 0x3F90AD3A_E322DA11
// atanhi[]/atanlo[] indexed by reduction interval id (0.5, 1.0, 1.5, inf).
const ATANHI = [4.63647609000806093515e-01, 7.85398163397448278999e-01, 9.82793723247329054082e-01, 1.57079632679489655800e+00];
const ATANLO = [2.26987774529616870924e-17, 3.06161699786838301793e-17, 1.39033110312309984516e-17, 6.12323399573676603587e-17];
const ATAN_2P66 = 73786976294838206464; //   2^66 reduction cutoff (|x|>=2^66)

// FDLIBM e_acos.c — rational kernels pS0..pS5 / qS1..qS4 (pi, pi/2 reuse above).
const PS0 = 1.66666666666666657415e-01; //  0x3FC55555_55555555
const PS1 = -3.25565818622400915405e-01; // 0xBFD4D612_03EB6F7D
const PS2 = 2.01212532134862925881e-01; //  0x3FC9C155_0E884455
const PS3 = -4.00555345006794114027e-02; // 0xBFA48228_B5688F3B
const PS4 = 7.91534994289814532176e-04; //  0x3F49EFE0_7501B288
const PS5 = 3.47933107596021167570e-05; //  0x3F023DE1_0DFDF709
const QS1 = -2.40339491173441421878e+00; // 0xC0033A27_1C8A2D4B
const QS2 = 2.02094576023350569471e+00; //  0x40002AE5_9C598AC8
const QS3 = -6.88283971605453293030e-01; // 0xBFE6066C_1B8D0159
const QS4 = 7.70381505559019352791e-02; //  0x3FB3B8C5_B12E9282

// FDLIBM e_exp.c — Cody-Waite ln2 split (hi/lo, both signs, indexed by sign
// bit), 1/ln2, P1..P5 kernel, over/underflow thresholds, 2^-1000 scaler.
const EXP_HALF = [0.5, -0.5];
const EXP_LN2HI = [6.93147180369123816490e-01, -6.93147180369123816490e-01]; // ±0x3FE62E42_FEE00000
const EXP_LN2LO = [1.90821492927058770002e-10, -1.90821492927058770002e-10]; // ±0x3DEA39EF_35793C76
const INVLN2 = 1.44269504088896338700e+00; // e_exp.c invln2 / e_pow.c ivln2  0x3FF71547_652B82FE
const EXP_P1 = 1.66666666666666019037e-01; //  0x3FC55555_5555553E
const EXP_P2 = -2.77777777770155933842e-03; // 0xBF66C16C_16BEBD93
const EXP_P3 = 6.61375632143793436117e-05; //  0x3F11566A_AF25DE2C
const EXP_P4 = -1.65339022054652515390e-06; // 0xBEBBBD41_C5D26BF1
const EXP_P5 = 4.13813679705723846039e-08; //  0x3E663769_72BEA4D0
const EXP_OVERFLOW = 7.09782712893383973096e+02; //  o_threshold 0x40862E42_FEFA39EF
const EXP_UNDERFLOW = -7.45133219101941108420e+02; // u_threshold 0xC0874910_D52D3051
const TWOM1000 = 9.33263618503218878990e-302; //     2^-1000     0x01700000_00000000

// FDLIBM e_log.c — mantissa/exponent split kernel Lg1..Lg7. ln2 here is the
// SAME truncated double-double as e_exp.c (hi has its low 21 mantissa bits 0).
const LOG_LN2_HI = 6.93147180369123816490e-01; // 0x3FE62E42_FEE00000
const LOG_LN2_LO = 1.90821492927058770002e-10; // 0x3DEA39EF_35793C76
const TWO54 = 1.80143985094819840000e+16; //      2^54  0x43500000_00000000
const LG1 = 6.666666666666735130e-01; // 0x3FE55555_55555593
const LG2 = 3.999999999940941908e-01; // 0x3FD99999_9997FA04
const LG3 = 2.857142874366239149e-01; // 0x3FD24924_94229359
const LG4 = 2.2222198432149787e-01; //  0x3FCC71C5_1D8E78B0
const LG5 = 1.818357216161805012e-01; // 0x3FC74664_96CB03DE
const LG6 = 1.531383769920937332e-01; // 0x3FC39A09_D078C69F
const LG7 = 1.479819860511658591e-01; // 0x3FC2F112_DF3E5244
const LOG_ONE_THIRD = 0.3333333333333333; // 1/3 nearest double, e_log.c near-1 path

// FDLIBM e_pow.c — extra-precision (head/tail) log2/exp2 path. Interval
// selectors bp/dp, log poly L1..L6, exp poly P1..P5 (identical to e_exp.c's,
// transcribed separately for a faithful per-file port), ln2 & 1/ln2 as 24-bit
// head + tail, cp = 2/(3 ln2), and the over/underflow tie guard ovt.
const BP = [1.0, 1.5];
const DP_H = [0.0, 5.84962487220764160156e-01]; // dp_h[1] 0x3FE2B803_40000000
const DP_L = [0.0, 1.35003920212974897128e-08]; // dp_l[1] 0x3E4CFDEB_43CFD006
const POW_L1 = 5.99999999999994648725e-01; //  0x3FE33333_33333303
const POW_L2 = 4.28571428578550184252e-01; //  0x3FDB6DB6_DB6FABFF
const POW_L3 = 3.33333329818377432918e-01; //  0x3FD55555_518F264D
const POW_L4 = 2.72728123808534006489e-01; //  0x3FD17460_A91D4101
const POW_L5 = 2.3066074577556175e-01; //   0x3FCD864A_93C9DB65
const POW_L6 = 2.06975017800338417784e-01; //  0x3FCA7E28_4A454EEF
const POW_P1 = 1.66666666666666019037e-01; //  0x3FC55555_5555553E
const POW_P2 = -2.77777777770155933842e-03; // 0xBF66C16C_16BEBD93
const POW_P3 = 6.61375632143793436117e-05; //  0x3F11566A_AF25DE2C
const POW_P4 = -1.65339022054652515390e-06; // 0xBEBBBD41_C5D26BF1
const POW_P5 = 4.13813679705723846039e-08; //  0x3E663769_72BEA4D0
const POW_LG2 = 6.93147180559945286227e-01; //   lg2    0x3FE62E42_FEFA39EF
const POW_LG2_H = 6.93147182464599609375e-01; //  lg2_h  0x3FE62E43_00000000
const POW_LG2_L = -1.90465429995776804525e-09; // lg2_l  0xBE205C61_0CA86C39
const POW_OVT = 8.0085662595372944372e-17; //     ovt    over/underflow tie
const POW_CP = 9.61796693925975554329e-01; //     cp   = 2/(3 ln2) 0x3FEEC709_DC3A03FD
const POW_CP_H = 9.61796700954437255859e-01; //   cp_h   0x3FEEC709_E0000000
const POW_CP_L = -7.02846165095275826516e-09; //  cp_l   0xBE3E2FE0_145B01F5
const POW_IVLN2_H = 1.44269502162933349609e+00; // ivln2_h 0x3FF71547_60000000
const POW_IVLN2_L = 1.92596299112661746887e-08; // ivln2_l 0x3E54AE0B_F85DDF44
const TWO53 = 9007199254740992.0; //  2^53 0x43400000_00000000
const POW_HUGE = 1.0e300;

// ── argument reduction ────────────────────────────────────────────────────
// Error-compensated Cody-Waite: reduce x to r in [-pi/4, pi/4] with quadrant
// index q in {0,1,2,3} such that x = r + q*(pi/2) + k*2pi.  Results are written
// to module-scoped scratch (`_quad`, `_rem`) to avoid per-call allocation; JS
// is single-threaded so this is safe and fully deterministic.
let _quad = 0;
let _rem = 0;

function reducePiO2(x: number): void {
  const n = Math.round(x * INV_PIO2);

  // n * PIO2_HI carried exactly as (p + pe) via a Dekker two-product.
  const p = n * PIO2_HI;
  const cn = SPLIT * n;
  const nhi = cn - (cn - n);
  const nlo = n - nhi;
  const ch = SPLIT * PIO2_HI;
  const hhi = ch - (ch - PIO2_HI);
  const hlo = PIO2_HI - hhi;
  const pe = nhi * hhi - p + nhi * hlo + nlo * hhi + nlo * hlo;

  // Full low part of n*(pi/2): rounding error of n*PIO2_HI plus n*PIO2_LO.
  const plo = pe + n * PIO2_LO;

  // r = x - n*(pi/2). (x - p) is exact by Sterbenz (p is within a factor 2 of
  // x); the two-sum residual e captures any exception, then subtract plo.
  const s = x - p;
  const e = x - s - p;
  _rem = s + (e - plo);
  _quad = n & 3;
}

// ── kernels (valid for |r| <= pi/4) ───────────────────────────────────────

/** FDLIBM k_sin.c __kernel_sin with the low-order tail term omitted (y = 0). */
function kernelSin(r: number): number {
  const z = r * r;
  const v = z * r;
  const p = S2 + z * (S3 + z * (S4 + z * (S5 + z * S6)));
  return r + v * (S1 + z * p);
}

/** FDLIBM k_cos.c __kernel_cos, simplified for y = 0. */
function kernelCos(r: number): number {
  const z = r * r;
  const p = z * (C1 + z * (C2 + z * (C3 + z * (C4 + z * (C5 + z * C6)))));
  return 1.0 - (0.5 * z - z * p);
}

// ── exact IEEE-754 bit access (DataView punning) ──────────────────────────
// ECMA-262 fully specifies the byte layout of Float64 in a DataView, so this
// float<->word punning is exact and deterministic on every engine. The FDLIBM
// exp/log/pow kernels below read and reconstruct the sign/exponent/mantissa
// words directly. Module-scoped scratch (single-threaded JS) mirrors the
// reduction-scratch pattern used above. High word = big-endian bytes 0..3.
const _bits = new DataView(new ArrayBuffer(8));
function highWord(v: number): number { _bits.setFloat64(0, v); return _bits.getUint32(0); }
function lowWord(v: number): number { _bits.setFloat64(0, v); return _bits.getUint32(4); }
function fromWords(hi: number, lo: number): number {
  _bits.setUint32(0, hi >>> 0); _bits.setUint32(4, lo >>> 0); return _bits.getFloat64(0);
}
function setHighWord(v: number, hi: number): number {
  _bits.setFloat64(0, v); _bits.setUint32(0, hi >>> 0); return _bits.getFloat64(0);
}
function setLowWord(v: number, lo: number): number {
  _bits.setFloat64(0, v); _bits.setUint32(4, lo >>> 0); return _bits.getFloat64(0);
}

// ── atan kernel (FDLIBM s_atan.c) ─────────────────────────────────────────
/** __ieee754_atan; internal, consumed by atan2. Not exported. */
function atanImpl(x: number): number {
  if (x !== x) return NaN;
  const ax = x < 0 ? -x : x;
  if (ax >= ATAN_2P66) return x > 0 ? ATANHI[3] + ATANLO[3] : -ATANHI[3] - ATANLO[3];
  let id: number;
  let xr: number;
  const neg = x < 0;
  if (ax < 0.4375) {
    if (ax < SIN_TINY) return x; // |x| < 2^-27 (SIN_TINY): atan(x) == x, sign kept
    id = -1;
    xr = x;
  } else if (ax < 1.1875) {
    if (ax < 0.6875) { id = 0; xr = (2.0 * ax - 1.0) / (2.0 + ax); } // 7/16<=|x|<11/16
    else { id = 1; xr = (ax - 1.0) / (ax + 1.0); } //                  11/16<=|x|<19/16
  } else if (ax < 2.4375) {
    id = 2; xr = (ax - 1.5) / (1.0 + 1.5 * ax); //                     19/16<=|x|<2.4375
  } else {
    id = 3; xr = -1.0 / ax; //                                        2.4375<=|x|<2^66
  }
  const z = xr * xr;
  const w = z * z;
  // sum_{i=0..10} aT[i]*z^(i+1) split into odd (s1) and even (s2) parts
  const s1 = z * (AT0 + w * (AT2 + w * (AT4 + w * (AT6 + w * (AT8 + w * AT10)))));
  const s2 = w * (AT1 + w * (AT3 + w * (AT5 + w * (AT7 + w * AT9))));
  if (id < 0) return xr - xr * (s1 + s2);
  const z2 = ATANHI[id] - ((xr * (s1 + s2) - ATANLO[id]) - xr);
  return neg ? -z2 : z2;
}

// ── scale-by-2^n (FDLIBM s_scalbn.c, used by pow's subnormal tail) ─────────
/** 2^m as a double for -1022 <= m <= 1023 via exact exponent-bit construction. */
function pow2i(m: number): number { return fromWords(((0x3ff + m) << 20) >>> 0, 0); }
function scalbn(x: number, n: number): number {
  if (x === 0 || x !== x || x === Infinity || x === -Infinity) return x;
  let k = n;
  if (k > 1023) { x *= pow2i(1023); k -= 1023; if (k > 1023) { x *= pow2i(1023); k -= 1023; if (k > 1023) k = 1023; } return x * pow2i(k); }
  if (k < -1022) { x *= pow2i(-1022); k += 1022; if (k < -1022) { x *= pow2i(-1022); k += 1022; if (k < -1022) k = -1022; } return x * pow2i(k); }
  return x * pow2i(k);
}

// ── public functions ──────────────────────────────────────────────────────

/** Portable sine. Deterministic across engines; <= 2 ulp vs Math.sin for |x| <= 2^26. */
export function sin(x: number): number {
  if (x - x !== 0) return NaN; // +/-Infinity or NaN
  const ax = x < 0 ? -x : x;
  if (ax < SIN_TINY) return x; // sin(x)==x here; preserves sign of zero
  reducePiO2(x);
  const r = _rem;
  switch (_quad) {
    case 0: return kernelSin(r);
    case 1: return kernelCos(r);
    case 2: return -kernelSin(r);
    default: return -kernelCos(r);
  }
}

/** Portable cosine. Deterministic across engines; <= 2 ulp vs Math.cos for |x| <= 2^26. */
export function cos(x: number): number {
  if (x - x !== 0) return NaN; // +/-Infinity or NaN
  reducePiO2(x);
  const r = _rem;
  switch (_quad) {
    case 0: return kernelCos(r);
    case 1: return -kernelSin(r);
    case 2: return -kernelCos(r);
    default: return kernelSin(r);
  }
}

/**
 * Portable Euclidean norm (2- or 3-argument). Deterministic across engines;
 * <= 2 ulp vs Math.hypot. Scales by the largest magnitude to avoid overflow /
 * underflow, using exact divisions and a single Math.sqrt.
 */
export function hypot(x: number, y: number, z?: number): number {
  const zz = z === undefined ? 0 : z;
  // Infinity dominates NaN, matching IEEE-754 / Math.hypot.
  if (x === Infinity || x === -Infinity || y === Infinity || y === -Infinity || zz === Infinity || zz === -Infinity) {
    return Infinity;
  }
  if (x !== x || y !== y || zz !== zz) return NaN;

  const ax = x < 0 ? -x : x;
  const ay = y < 0 ? -y : y;
  const az = zz < 0 ? -zz : zz;
  let m = ax > ay ? ax : ay;
  if (az > m) m = az;
  if (m === 0) return 0;

  const rx = ax / m;
  const ry = ay / m;
  const rz = az / m;
  return m * Math.sqrt(rx * rx + ry * ry + rz * rz);
}

/**
 * Portable atan2 (FDLIBM e_atan2.c). Deterministic across engines; <= 2 ulp vs
 * Math.atan2. Signed-zero and infinity quadrants match IEEE-754 / Math.atan2.
 */
export function atan2(y: number, x: number): number {
  if (x !== x || y !== y) return NaN;
  if (x === 1) return atanImpl(y); // x == 1.0: atan2(y,1) == atan(y)
  const sy = highWord(y) >>> 31; // sign bit of y (1 if negative, incl. -0)
  const sx = highWord(x) >>> 31; // sign bit of x
  const m = (sy & 1) | ((sx << 1) & 2); // 2*sign(x) + sign(y)
  if (y === 0) {
    switch (m) {
      case 0: case 1: return y; //          atan2(+-0, +anything) = +-0
      case 2: return PI + TINY; //          atan2(+0, -anything) = pi
      default: return -PI - TINY; //        atan2(-0, -anything) = -pi
    }
  }
  if (x === 0) return sy ? -PIO2_HI - TINY : PIO2_HI + TINY; // atan2(y, +-0) = +-pi/2
  if (x === Infinity || x === -Infinity) {
    if (y === Infinity || y === -Infinity) {
      switch (m) {
        case 0: return PIO4 + TINY; //        atan2(+inf, +inf)
        case 1: return -PIO4 - TINY; //       atan2(-inf, +inf)
        case 2: return 3.0 * PIO4 + TINY; //  atan2(+inf, -inf)
        default: return -3.0 * PIO4 - TINY; //atan2(-inf, -inf)
      }
    }
    switch (m) {
      case 0: return 0.0; //   atan2(+finite, +inf)
      case 1: return -0.0; //  atan2(-finite, +inf)
      case 2: return PI + TINY; //   atan2(+finite, -inf)
      default: return -PI - TINY; // atan2(-finite, -inf)
    }
  }
  if (y === Infinity || y === -Infinity) return sy ? -PIO2_HI - TINY : PIO2_HI + TINY;
  const ax = x < 0 ? -x : x;
  const ay = y < 0 ? -y : y;
  const k = (highWord(ay) - highWord(ax)) >> 20; // exponent difference of |y|,|x|
  let z: number;
  if (k > 60) z = PIO2_HI + 0.5 * PI_LO; //          |y/x| > 2^60
  else if (sx === 1 && k < -60) z = 0.0; //          x < 0 and |y/x| < 2^-60
  else z = atanImpl(ay / ax);
  switch (m) {
    case 0: return z; //                  atan2(+, +)
    case 1: return -z; //                 atan2(-, +)
    case 2: return PI - (z - PI_LO); //   atan2(+, -)
    default: return (z - PI_LO) - PI; //  atan2(-, -)
  }
}

/**
 * Portable acos (FDLIBM e_acos.c). Deterministic across engines; <= 2 ulp vs
 * Math.acos over |x| <= 1. Returns NaN for |x| > 1.
 */
export function acos(x: number): number {
  if (x !== x) return NaN;
  const hx = highWord(x);
  const ix = hx & 0x7fffffff;
  const negX = (hx >>> 31) === 1;
  if (ix >= 0x3ff00000) { // |x| >= 1
    if (ix === 0x3ff00000 && lowWord(x) === 0) return negX ? PI + 2.0 * PIO2_LO : 0.0; // acos(-1)=pi, acos(1)=0
    return NaN; // |x| > 1
  }
  if (ix < 0x3fe00000) { // |x| < 0.5
    if (ix <= 0x3c600000) return PIO2_HI + PIO2_LO; // |x| < 2^-57: acos ~ pi/2
    const z = x * x;
    const p = z * (PS0 + z * (PS1 + z * (PS2 + z * (PS3 + z * (PS4 + z * PS5)))));
    const q = 1.0 + z * (QS1 + z * (QS2 + z * (QS3 + z * QS4)));
    const r = p / q;
    return PIO2_HI - (x - (PIO2_LO - x * r));
  }
  if (negX) { // x <= -0.5
    const z = (1.0 + x) * 0.5;
    const p = z * (PS0 + z * (PS1 + z * (PS2 + z * (PS3 + z * (PS4 + z * PS5)))));
    const q = 1.0 + z * (QS1 + z * (QS2 + z * (QS3 + z * QS4)));
    const s = Math.sqrt(z);
    const r = p / q;
    const w = r * s - PIO2_LO;
    return PI - 2.0 * (s + w);
  }
  // x >= 0.5
  const z = (1.0 - x) * 0.5;
  const s = Math.sqrt(z);
  const df = fromWords(highWord(s), 0); // s with low 32 bits cleared (24-bit head)
  const c = (z - df * df) / (s + df);
  const p = z * (PS0 + z * (PS1 + z * (PS2 + z * (PS3 + z * (PS4 + z * PS5)))));
  const q = 1.0 + z * (QS1 + z * (QS2 + z * (QS3 + z * QS4)));
  const r = p / q;
  const w = r * s + c;
  return 2.0 * (df + w);
}

/**
 * Portable exp (FDLIBM e_exp.c). Deterministic across engines; <= 2 ulp vs
 * Math.exp. Cody-Waite ln2 reduction + P1..P5 kernel + exact 2^k scaling.
 */
export function exp(x: number): number {
  if (x !== x) return NaN;
  const hw = highWord(x);
  const xsb = (hw >>> 31) & 1; // sign bit
  const hx = hw & 0x7fffffff; // |x| high word
  if (hx >= 0x40862e42) { // |x| >= 709.78...
    if (hx >= 0x7ff00000) return xsb === 0 ? x : 0.0; // exp(+inf)=inf, exp(-inf)=0
    if (x > EXP_OVERFLOW) return Infinity; // overflow
    if (x < EXP_UNDERFLOW) return 0.0; //    underflow
  }
  let k = 0;
  let hi = 0.0;
  let lo = 0.0;
  let xr = x;
  if (hx > 0x3fd62e42) { // |x| > 0.5*ln2
    if (hx < 0x3ff0a2b2) { // |x| < 1.5*ln2
      hi = x - EXP_LN2HI[xsb];
      lo = EXP_LN2LO[xsb];
      k = 1 - xsb - xsb;
    } else {
      k = Math.trunc(INVLN2 * x + EXP_HALF[xsb]);
      const t = k;
      hi = x - t * EXP_LN2HI[0]; // t*ln2HI exact here
      lo = t * EXP_LN2LO[0];
    }
    xr = hi - lo;
  } else if (hx < 0x3e300000) {
    return 1.0 + x; // |x| < 2^-28: exp(x) ~ 1 + x
  }
  const t = xr * xr;
  const c = xr - t * (EXP_P1 + t * (EXP_P2 + t * (EXP_P3 + t * (EXP_P4 + t * EXP_P5))));
  if (k === 0) return 1.0 - ((xr * c) / (c - 2.0) - xr);
  let y = 1.0 - ((lo - (xr * c) / (2.0 - c)) - hi);
  // scale y by 2^k by adding k to y's exponent field (exact, overflow-safe)
  if (k >= -1021) return setHighWord(y, (highWord(y) + (k << 20)) >>> 0);
  y = setHighWord(y, (highWord(y) + ((k + 1000) << 20)) >>> 0);
  return y * TWOM1000;
}

/**
 * Portable natural log (FDLIBM e_log.c). Deterministic across engines; <= 2 ulp
 * vs Math.log. log(+-0) = -Infinity; log(x<0) = NaN.
 */
export function log(x: number): number {
  if (x !== x) return NaN;
  let hx = highWord(x) | 0; // signed high word (needed for hx < 0x00100000 test)
  const lx = lowWord(x);
  let k = 0;
  let xx = x;
  if (hx < 0x00100000) { // x < 2^-1022 (subnormal, zero, or negative)
    if (((hx & 0x7fffffff) | lx) === 0) return -Infinity; // log(+-0)
    if (hx < 0) return NaN; //                              log(negative)
    k -= 54;
    xx = x * TWO54; // scale subnormal up
    hx = highWord(xx) | 0;
  }
  if (hx >= 0x7ff00000) return xx + xx; // +Infinity
  k += (hx >> 20) - 1023;
  hx &= 0x000fffff;
  const i = (hx + 0x95f64) & 0x100000; // choose mantissa in [sqrt(2)/2, sqrt(2))
  xx = fromWords((hx | (i ^ 0x3ff00000)) >>> 0, lowWord(xx));
  k += i >> 20;
  const f = xx - 1.0;
  const dk = k;
  if ((0x000fffff & (2 + hx)) < 3) { // |f| < 2^-20 (x very close to 1)
    if (f === 0) return k === 0 ? 0.0 : dk * LOG_LN2_HI + dk * LOG_LN2_LO;
    const R = f * f * (0.5 - LOG_ONE_THIRD * f);
    if (k === 0) return f - R;
    return dk * LOG_LN2_HI - ((R - dk * LOG_LN2_LO) - f);
  }
  const s = f / (2.0 + f);
  const z = s * s;
  const w = z * z;
  const t1 = w * (LG2 + w * (LG4 + w * LG6));
  const t2 = z * (LG1 + w * (LG3 + w * (LG5 + w * LG7)));
  const R = t2 + t1;
  const ii = (hx - 0x6147a) | (0x6b851 - hx);
  if (ii > 0) {
    const hfsq = 0.5 * f * f;
    if (k === 0) return f - (hfsq - s * (hfsq + R));
    return dk * LOG_LN2_HI - ((hfsq - (s * (hfsq + R) + dk * LOG_LN2_LO)) - f);
  }
  if (k === 0) return f - s * (f - R);
  return dk * LOG_LN2_HI - ((s * (f - R) - dk * LOG_LN2_LO) - f);
}

/**
 * Portable pow (FDLIBM e_pow.c). Deterministic across engines; <= 2 ulp vs
 * Math.pow over positive bases. Uses the extra-precision head/tail log2/exp2
 * refinement, so integer exponents are also <= 1 ulp AND exact for exactly
 * representable cases (e.g. pow(1.5,7), pow(2,10)) — this replaces the plan's
 * repeated-multiplication fast path, which measured up to 14 ulp vs Math.pow
 * (see report). Negative-base semantics match Math.pow: (x<0)^(non-int) = NaN.
 */
export function pow(x: number, y: number): number {
  const hx = highWord(x) | 0;
  const lx = lowWord(x);
  const hy = highWord(y) | 0;
  const ly = lowWord(y);
  let ix = hx & 0x7fffffff;
  const iy = hy & 0x7fffffff;
  if ((iy | ly) === 0) return 1.0; // x^0 = 1 (incl. NaN^0, per Math.pow)
  if (ix > 0x7ff00000 || (ix === 0x7ff00000 && lx !== 0) || iy > 0x7ff00000 || (iy === 0x7ff00000 && ly !== 0)) return x + y; // NaN

  // yisint: 0 = not int, 1 = odd int, 2 = even int (only needed when x < 0)
  let yisint = 0;
  if (hx < 0) {
    if (iy >= 0x43400000) yisint = 2;
    else if (iy >= 0x3ff00000) {
      const kk = (iy >> 20) - 0x3ff;
      if (kk > 20) {
        const j = ly >>> (52 - kk);
        if (((j << (52 - kk)) >>> 0) === ly) yisint = 2 - (j & 1);
      } else if (ly === 0) {
        const j = iy >>> (20 - kk);
        if (((j << (20 - kk)) >>> 0) === iy) yisint = 2 - (j & 1);
      }
    }
  }

  // special values of y
  if (ly === 0) {
    if (iy === 0x7ff00000) { // y = +-Infinity
      if (((ix - 0x3ff00000) | lx) === 0) return 1.0; // (+-1)^+-inf = 1
      if (ix >= 0x3ff00000) return hy >= 0 ? y : 0.0; // (|x|>1)^+-inf
      return hy < 0 ? -y : 0.0; //                       (|x|<1)^+-inf
    }
    if (iy === 0x3ff00000) return hy < 0 ? 1.0 / x : x; // y = +-1
    if (hy === 0x40000000) return x * x; //               y = 2 (exact)
    if (hy === 0x3fe00000 && hx >= 0) return Math.sqrt(x); // y = 0.5, x >= 0
  }

  let ax = x < 0 ? -x : x;
  // special values of x (+-0, +-inf, +-1)
  if (lx === 0 && (ix === 0x7ff00000 || ix === 0 || ix === 0x3ff00000)) {
    let z = ax;
    if (hy < 0) z = 1.0 / z;
    if (hx < 0) {
      if (((ix - 0x3ff00000) | yisint) === 0) z = (z - z) / (z - z); // (-1)^non-int = NaN
      else if (yisint === 1) z = -z; //                                 (x<0)^odd
    }
    return z;
  }

  let n = (hx >> 31) + 1; // 0 if x < 0, else 1
  if ((n | yisint) === 0) return (x - x) / (x - x); // (x<0)^non-int = NaN
  let s = 1.0; // sign of result
  if ((n | (yisint - 1)) === 0) s = -1.0; // (x<0)^odd -> negative

  let t: number, w: number, u: number, v: number, t1: number, t2: number, r: number, z: number, ph: number, pl: number, y1: number;
  if (iy > 0x41e00000) { // |y| > 2^31
    if (iy > 0x43f00000) { // |y| > 2^64: guaranteed over/underflow
      if (ix <= 0x3fefffff) return hy < 0 ? POW_HUGE * POW_HUGE : TINY * TINY;
      if (ix >= 0x3ff00000) return hy > 0 ? POW_HUGE * POW_HUGE : TINY * TINY;
    }
    if (ix < 0x3fefffff) return hy < 0 ? s * POW_HUGE * POW_HUGE : s * TINY * TINY;
    if (ix > 0x3ff00000) return hy > 0 ? s * POW_HUGE * POW_HUGE : s * TINY * TINY;
    // |1 - x| tiny: log(x) via x - x^2/2 + x^3/3 - x^4/4
    t = ax - 1.0;
    w = t * t * (0.5 - t * (LOG_ONE_THIRD - t * 0.25));
    u = POW_IVLN2_H * t;
    v = t * POW_IVLN2_L - w * INVLN2;
    t1 = u + v;
    t1 = setLowWord(t1, 0);
    t2 = v - (t1 - u);
  } else {
    let ss: number, s2: number, sh: number, sl: number, th: number, tl: number;
    n = 0;
    if (ix < 0x00100000) { ax *= TWO53; n -= 53; ix = highWord(ax); } // subnormal x
    n += (ix >> 20) - 0x3ff;
    const j = ix & 0x000fffff;
    ix = j | 0x3ff00000; // normalize mantissa into [1,2)
    let k: number;
    if (j <= 0x3988e) k = 0; //        |x| < sqrt(3/2)
    else if (j < 0xbb67a) k = 1; //    |x| < sqrt(3)
    else { k = 0; n += 1; ix -= 0x00100000; }
    ax = setHighWord(ax, ix);
    // ss = (ax - bp[k]) / (ax + bp[k]) as head/tail sh + sl
    u = ax - BP[k];
    v = 1.0 / (ax + BP[k]);
    ss = u * v;
    sh = setLowWord(ss, 0);
    th = setHighWord(0.0, (((ix >> 1) | 0x20000000) + 0x00080000 + (k << 18)) >>> 0);
    tl = ax - (th - BP[k]);
    sl = v * ((u - sh * th) - sh * tl);
    // log(ax) polynomial
    s2 = ss * ss;
    r = s2 * s2 * (POW_L1 + s2 * (POW_L2 + s2 * (POW_L3 + s2 * (POW_L4 + s2 * (POW_L5 + s2 * POW_L6)))));
    r += sl * (sh + ss);
    s2 = sh * sh;
    th = 3.0 + s2 + r;
    th = setLowWord(th, 0);
    tl = r - ((th - 3.0) - s2);
    u = sh * th;
    v = sl * th + tl * ss;
    ph = u + v;
    ph = setLowWord(ph, 0);
    pl = v - (ph - u);
    const zh = POW_CP_H * ph;
    const zl = POW_CP_L * ph + pl * POW_CP + DP_L[k];
    t = n;
    t1 = ((zh + zl) + DP_H[k]) + t;
    t1 = setLowWord(t1, 0);
    t2 = zl - (((t1 - t) - DP_H[k]) - zh);
  }

  // split y into y1 + (y - y1) and form (y1 + y2)*(t1 + t2)
  y1 = setLowWord(y, 0);
  pl = (y - y1) * t1 + y * t2;
  ph = y1 * t1;
  z = pl + ph;
  const jz = highWord(z) | 0;
  const iz = lowWord(z);
  if (jz >= 0x40900000) { // z >= 1024 -> overflow
    if (!(highWord(z) === 0x40900000 && iz === 0)) return s * POW_HUGE * POW_HUGE;
    if (pl + POW_OVT > z - ph) return s * POW_HUGE * POW_HUGE;
  } else if ((jz & 0x7fffffff) >= 0x4090cc00) { // z <= -1075 -> underflow
    if (!(highWord(z) === 0xc090cc00 && iz === 0)) return s * TINY * TINY;
    if (pl <= z - ph) return s * TINY * TINY;
  }

  // compute 2^(ph + pl)
  const i3 = highWord(z) & 0x7fffffff;
  let k3 = (i3 >> 20) - 0x3ff;
  n = 0;
  if (i3 > 0x3fe00000) { // |z| > 0.5: n = round(z)
    n = (highWord(z) | 0) + (0x00100000 >> (k3 + 1));
    k3 = ((n & 0x7fffffff) >> 20) - 0x3ff;
    t = setHighWord(0.0, (n & ~(0x000fffff >> k3)) >>> 0);
    n = ((n & 0x000fffff) | 0x00100000) >> (20 - k3);
    if ((highWord(z) | 0) < 0) n = -n;
    ph -= t;
  }
  t = pl + ph;
  t = setLowWord(t, 0);
  u = t * POW_LG2_H;
  v = (pl - (t - ph)) * POW_LG2 + t * POW_LG2_L;
  z = u + v;
  w = v - (z - u);
  t = z * z;
  t1 = z - t * (POW_P1 + t * (POW_P2 + t * (POW_P3 + t * (POW_P4 + t * POW_P5))));
  r = (z * t1) / (t1 - 2.0) - (w + z * w);
  z = 1.0 - (r - z);
  let j4 = highWord(z) | 0;
  j4 = j4 + (n << 20);
  if ((j4 >> 20) <= 0) z = scalbn(z, n); // subnormal result
  else z = setHighWord(z, j4 >>> 0);
  return s * z;
}
